'use client';

import { useEffect, useRef, useState } from 'react';
import cf from './CatalogFilters.module.css';

const AGE_OPTIONS = [5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function useOutsideClose(ref, onClose) {
  useEffect(() => {
    function handler(e) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target)) onClose();
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [ref, onClose]);
}

function dayTo2(day) {
  const d = (day || '').toString().trim().toLowerCase();
  if (!d) return '';
  if (d.startsWith('th')) return 'Th';
  if (d.startsWith('tu')) return 'Tu';
  if (d.startsWith('sa')) return 'Sa';
  if (d.startsWith('su')) return 'Su';
  if (d.startsWith('mo')) return 'Mo';
  if (d.startsWith('we')) return 'We';
  if (d.startsWith('fr')) return 'Fr';
  return day.toString().trim().slice(0, 2).replace(/^./, (c) => c.toUpperCase());
}

function formatSelectedDaysShort(selectedDays) {
  const list = Array.isArray(selectedDays) ? selectedDays : [];
  return list.map(dayTo2).filter(Boolean).join(', ');
}

function blockInvalidNumberInput(e) {
  if (e.key === '-' || e.key === 'e' || e.key === '+') e.preventDefault();
}

function handlePriceNumber(value, setter, enableFilter) {
  enableFilter(true);

  if (value === '') {
    setter('');
    return;
  }

  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    setter('');
    return;
  }

  setter(Math.min(100, Math.max(0, numeric)));
}

export default function CatalogFilters({
  ageFilterEnabled,
  setAgeFilterEnabled,
  selectedAges,
  setSelectedAges,

  priceFilterEnabled,
  setPriceFilterEnabled,
  minPrice,
  setMinPrice,
  maxPrice,
  setMaxPrice,

  dayFilterEnabled,
  setDayFilterEnabled,
  selectedDays,
  setSelectedDays,

  dateRange,
  setDateRange,
}) {
  const [open, setOpen] = useState(null); // 'age' | 'cost' | 'day' | 'date' | null
  const wrapRef = useRef(null);
  useOutsideClose(wrapRef, () => setOpen(null));

  function toggleAge(age) {
    setSelectedAges((prev) =>
      prev.includes(age) ? prev.filter((a) => a !== age) : [...prev, age]
    );
  }

  function toggleDay(day) {
    setSelectedDays((prev) =>
      prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day]
    );
  }

  const ageActive = ageFilterEnabled && selectedAges.length > 0;
  const costActive =
    priceFilterEnabled && (String(minPrice).trim() !== '' || String(maxPrice).trim() !== '');
  const dayActive = dayFilterEnabled && selectedDays.length > 0;
  const dateActive = dateRange && dateRange !== 'All upcoming dates';

  const anyActive = ageActive || costActive || dayActive || dateActive;

  function resetAll() {
    setAgeFilterEnabled(false);
    setSelectedAges([]);
    setPriceFilterEnabled(false);
    setMinPrice('');
    setMaxPrice('');
    setDayFilterEnabled(false);
    setSelectedDays([]);
    setDateRange('All upcoming dates');
    setOpen(null);
  }

  return (
    <div className={cf.bar} ref={wrapRef}>
      {/* AGE pill */}
      <div className={cf.pillWrap}>
        <button
          type="button"
          className={`${cf.pill} ${ageActive ? cf.pillActive : ''}`}
          onClick={() => setOpen(open === 'age' ? null : 'age')}
        >
          {ageActive ? `Ages: ${selectedAges.sort((a, b) => a - b).join(', ')}` : 'Age'}
          <span className={`${cf.caret} ${open === 'age' ? cf.caretUp : ''}`} />
        </button>

        {open === 'age' && (
          <div className={cf.popover}>
            <p className={cf.popoverLabel}>Select ages</p>
            <div className={cf.grid}>
              {AGE_OPTIONS.map((n) => {
                const checked = selectedAges.includes(n);
                return (
                  <button
                    key={n}
                    type="button"
                    className={`${cf.ageChip} ${checked ? cf.ageChipActive : ''}`}
                    onClick={() => {
                      setAgeFilterEnabled(true);
                      toggleAge(n);
                    }}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
            {selectedAges.length > 0 && (
              <button
                type="button"
                className={cf.clearBtn}
                onClick={() => {
                  setSelectedAges([]);
                  setAgeFilterEnabled(false);
                }}
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* COST pill */}
      <div className={cf.pillWrap}>
        <button
          type="button"
          className={`${cf.pill} ${costActive ? cf.pillActive : ''}`}
          onClick={() => setOpen(open === 'cost' ? null : 'cost')}
        >
          {costActive ? `$${minPrice || '0'} – $${maxPrice || '100'}` : 'Cost'}
          <span className={`${cf.caret} ${open === 'cost' ? cf.caretUp : ''}`} />
        </button>

        {open === 'cost' && (
          <div className={cf.popover}>
            <p className={cf.popoverLabel}>Price range</p>
            <div className={cf.row}>
              <div className={cf.inputWrap}>
                <span className={cf.inputPrefix}>$</span>
                <input
                  className={cf.input}
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  placeholder="Min"
                  value={minPrice}
                  onKeyDown={blockInvalidNumberInput}
                  onChange={(e) =>
                    handlePriceNumber(
                      e.target.value,
                      setMinPrice,
                      setPriceFilterEnabled
                    )
                  }
                />
              </div>

              <span className={cf.rangeDash}>–</span>

              <div className={cf.inputWrap}>
                <span className={cf.inputPrefix}>$</span>
                <input
                  className={cf.input}
                  type="number"
                  min="0"
                  max="100"
                  step="1"
                  placeholder="Max"
                  value={maxPrice}
                  onKeyDown={blockInvalidNumberInput}
                  onChange={(e) =>
                    handlePriceNumber(
                      e.target.value,
                      setMaxPrice,
                      setPriceFilterEnabled
                    )
                  }
                />
              </div>
            </div>

            {costActive && (
              <button
                type="button"
                className={cf.clearBtn}
                onClick={() => {
                  setMinPrice('');
                  setMaxPrice('');
                  setPriceFilterEnabled(false);
                }}
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* DAY pill */}
      <div className={cf.pillWrap}>
        <button
          type="button"
          className={`${cf.pill} ${dayActive ? cf.pillActive : ''}`}
          onClick={() => setOpen(open === 'day' ? null : 'day')}
        >
          {dayActive ? `Days: ${formatSelectedDaysShort(selectedDays)}` : 'Day'}
          <span className={`${cf.caret} ${open === 'day' ? cf.caretUp : ''}`} />
        </button>

        {open === 'day' && (
          <div className={cf.popoverBigBox}>
            <p className={cf.popoverLabel}>Select days</p>

            <div className={cf.dateOptions}>
              {DAYS_OF_WEEK.map((d) => {
                const checked = selectedDays.includes(d);
                return (
                  <button
                    key={d}
                    type="button"
                    className={`${cf.dateOption} ${checked ? cf.dateOptionActive : ''}`}
                    onClick={() => {
                      setDayFilterEnabled(true);
                      toggleDay(d);
                    }}
                  >
                    {d}
                  </button>
                );
              })}
            </div>

            {selectedDays.length > 0 && (
              <button
                type="button"
                className={cf.clearBtn}
                onClick={() => {
                  setSelectedDays([]);
                  setDayFilterEnabled(false);
                }}
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      {/* DATE pill */}
      <div className={cf.pillWrap}>
        <button
          type="button"
          className={`${cf.pill} ${dateActive ? cf.pillActive : ''}`}
          onClick={() => setOpen(open === 'date' ? null : 'date')}
        >
          {dateActive ? dateRange : 'Date'}
          <span className={`${cf.caret} ${open === 'date' ? cf.caretUp : ''}`} />
        </button>

        {open === 'date' && (
          <div className={`${cf.popover} ${cf.popoverLeft}`}>
            <p className={cf.popoverLabel}>When</p>
            <div className={cf.dateOptions}>
              {['All upcoming dates', 'Today', 'Next 7 days', 'Next 14 days'].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`${cf.dateOption} ${
                    dateRange === opt ? cf.dateOptionActive : ''
                  }`}
                  onClick={() => {
                    setDateRange(opt);
                    setOpen(null);
                  }}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {anyActive && (
        <button type="button" className={cf.reset} onClick={resetAll}>
          Reset all
        </button>
      )}
    </div>
  );
}